import { z } from 'zod'
import type { Rarity } from './card'

/**
 * Os acabamentos oficiais de uma impressao: o `set_rarity` do impresso. ADR 0061 secao 2, docs/02 secao 5.2.
 *
 * So entra nome oficial. Cada acabamento tem um NIVEL, que e uma das cinco raridades de hoje: nenhuma raridade
 * nova nasce para servir a versao, e o nivel e o que decide o corte "igual ou acima da casa" do pacote e o
 * fator de preco.
 */
export const FINISHES = [
  { code: 'COMMON', name: 'Common', tier: 'COMMON' },
  { code: 'RARE', name: 'Rare', tier: 'RARE' },
  { code: 'STARFOIL_RARE', name: 'Starfoil Rare', tier: 'RARE' },
  { code: 'SHATTERFOIL_RARE', name: 'Shatterfoil Rare', tier: 'RARE' },
  { code: 'MOSAIC_RARE', name: 'Mosaic Rare', tier: 'RARE' },
  { code: 'DT_NORMAL_PARALLEL_RARE', name: 'Duel Terminal Normal Parallel Rare', tier: 'RARE' },
  { code: 'DT_RARE_PARALLEL_RARE', name: 'Duel Terminal Rare Parallel Rare', tier: 'RARE' },
  { code: 'SUPER_RARE', name: 'Super Rare', tier: 'SUPER' },
  { code: 'DT_SUPER_PARALLEL_RARE', name: 'Duel Terminal Super Parallel Rare', tier: 'SUPER' },
  { code: 'ULTRA_RARE', name: 'Ultra Rare', tier: 'ULTRA' },
  { code: 'ULTRA_PARALLEL_RARE', name: 'Ultra Parallel Rare', tier: 'ULTRA' },
  { code: 'DT_ULTRA_PARALLEL_RARE', name: 'Duel Terminal Ultra Parallel Rare', tier: 'ULTRA' },
  { code: 'PHARAOHS_RARE', name: "Ultra Rare (Pharaoh's Rare)", tier: 'ULTRA' },
  { code: 'GOLD_RARE', name: 'Gold Rare', tier: 'ULTRA' },
  { code: 'PLATINUM_RARE', name: 'Platinum Rare', tier: 'ULTRA' },
  { code: 'PREMIUM_GOLD_RARE', name: 'Premium Gold Rare', tier: 'ULTRA' },
  { code: 'SECRET_RARE', name: 'Secret Rare', tier: 'SECRET' },
  { code: 'PRISMATIC_SECRET_RARE', name: 'Prismatic Secret Rare', tier: 'SECRET' },
  { code: 'GOLD_SECRET_RARE', name: 'Gold Secret Rare', tier: 'SECRET' },
  { code: 'EXTRA_SECRET_RARE', name: 'Extra Secret Rare', tier: 'SECRET' },
  { code: 'PLATINUM_SECRET_RARE', name: 'Platinum Secret Rare', tier: 'SECRET' },
  { code: 'QUARTER_CENTURY_SECRET_RARE', name: 'Quarter Century Secret Rare', tier: 'SECRET' },
  { code: 'ULTIMATE_RARE', name: 'Ultimate Rare', tier: 'SECRET' },
  { code: 'GHOST_RARE', name: 'Ghost Rare', tier: 'SECRET' },
  { code: 'GHOST_GOLD_RARE', name: 'Ghost/Gold Rare', tier: 'SECRET' },
  { code: 'STARLIGHT_RARE', name: 'Starlight Rare', tier: 'SECRET' },
  { code: 'COLLECTORS_RARE', name: "Collector's Rare", tier: 'SECRET' },
] as const satisfies readonly { code: string; name: string; tier: Rarity }[]

export type Finish = (typeof FINISHES)[number]['code']

export const FINISH_CODES = FINISHES.map((f) => f.code) as [Finish, ...Finish[]]

const BY_CODE = new Map<string, (typeof FINISHES)[number]>(FINISHES.map((f) => [f.code, f]))

export const isFinish = (code: string): code is Finish => BY_CODE.has(code)

/** O nivel do acabamento: a raridade de hoje em que ele se encaixa (ADR 0061 secao 2). */
export const finishTier = (finish: Finish): Rarity => BY_CODE.get(finish)!.tier

/** O nome oficial do acabamento, como o impresso o escreve. */
export const finishName = (finish: Finish): string => BY_CODE.get(finish)!.name

/**
 * Grafias do YGOPRODeck que divergem do nome oficial e que ja foram vistas no catalogo. A correcao e explicita:
 * qualquer outro nome desconhecido continua desconhecido.
 */
const KNOWN_MISSPELLINGS: Readonly<Record<string, Finish>> = {
  'PLatinum Secret Rare': 'PLATINUM_SECRET_RARE',
}

/** Short Print e Super Short Print sao tipo de impressao da Comum, e nao acabamento (ADR 0045 secao 4). */
const PRINT_TYPE_NAMES = new Set(['Short Print', 'Super Short Print'])

const BY_NAME = new Map<string, Finish>(FINISHES.map((f) => [f.name, f.code]))

export type SetRarityReading =
  { kind: 'FINISH'; finish: Finish } | { kind: 'PRINT_TYPE' } | { kind: 'UNKNOWN'; name: string }

/**
 * `set_rarity` oficial -> acabamento. Short Print e Super Short Print viram a Comum (sao impressao dela); nome
 * fora da tabela volta como desconhecido, com o nome, e quem le decide o que fazer com ele — nunca vira Comum
 * em silencio.
 */
export function finishOfSetRarity(setRarity: string): SetRarityReading {
  if (PRINT_TYPE_NAMES.has(setRarity)) return { kind: 'PRINT_TYPE' }
  const finish = BY_NAME.get(setRarity) ?? KNOWN_MISSPELLINGS[setRarity]
  return finish === undefined ? { kind: 'UNKNOWN', name: setRarity } : { kind: 'FINISH', finish }
}

const TIER_ORDER: Readonly<Record<Rarity, number>> = { COMMON: 0, RARE: 1, SUPER: 2, ULTRA: 3, SECRET: 4 }

/** `a` igual ou acima de `b` na escala das raridades. */
export const tierAtLeast = (a: Rarity, b: Rarity): boolean => TIER_ORDER[a] >= TIER_ORDER[b]

/** A versao de casa: a ausencia de versao (ADR 0061 secao 1). E o valor que a colecao grava para ela. */
export const HOME_VERSION = ''

/** O identificador de uma versao: o passcode da arte e o acabamento, unico dentro da carta. */
export const versionIdOf = (art: number, finish: Finish): string => `${art}-${finish}`

/**
 * Uma versao de uma carta: uma impressao oficial, arte e acabamento (ADR 0061 secao 1). `sets` sao os codigos
 * das impressoes em que ela saiu, so informativos.
 */
export const cardVersionSchema = z
  .object({
    id: z.string().min(1),
    art: z.number().int().positive(),
    finish: z.enum(FINISH_CODES),
    sets: z.array(z.string().min(1)).min(1),
  })
  .refine((v) => v.id === versionIdOf(v.art, v.finish), { message: 'id diferente de <arte>-<acabamento>' })
export type CardVersion = z.infer<typeof cardVersionSchema>
